import { isFulfillable } from './promo-fulfillment-utils';
import { pipe } from './promo-utils';
import {
  MAX_NUM_TIERS_IN_PROMOTION,
  NVALUE_REFINEMENT_SEPARATOR,
  MA, EXPERIENCE_TAGS,
} from './constants';

const isValidBogoData = (promotion) => {
  const { subExperienceTag, reward } = promotion;

  switch (subExperienceTag) {
  case 'buyOneGetOne':
    return true;
  case 'buyOneGetPercentageOff':
    return Boolean(
      reward.tiers.length === 1
        && reward.tiers[0].minPurchaseQuantity === 1
        && reward.tiers[0].rewardPercent
    );
  case 'buyOneGetDollarOff':
    return Boolean(
      reward.tiers.length === 1
        && reward.tiers[0].minPurchaseQuantity === 1
        && reward.tiers[0].rewardAmountPerOrder
    );
  default:
    return false;
  }
};

/**
 * Given an array of reward tiers, this function will return true
 * if all reward tiers contain *at least* the same properties in the first reward tier.
 * @param {Array} rewardTiers
 * @returns {boolean} hasSameTiers
 */
const allTiersHaveSameDetails = (rewardTiers) => {
  const {
    minPurchaseQuantity,
    minPurchaseAmount,
    rewardPercent,
    rewardAmountPerOrder,
    rewardAmountPerItem,
    rewardFixedPrice
  } = rewardTiers[0];

  return rewardTiers.every((tier) => {
    if (rewardPercent && !tier.rewardPercent) return false;
    if (rewardAmountPerItem && !tier.rewardAmountPerItem) return false;
    if (rewardAmountPerOrder && !tier.rewardAmountPerOrder) return false;
    if (minPurchaseAmount && !tier.minPurchaseAmount) return false;
    if (minPurchaseQuantity && !tier.minPurchaseQuantity) return false;
    if (rewardFixedPrice && !tier.rewardFixedPrice) return false;
    return true;
  });
};

const getMinimumPrice = (rewardTiers) => {
  return rewardTiers.reduce((currentMinPrice, { rewardFixedPrice }) => Math.max(currentMinPrice, rewardFixedPrice), 0);
};

const isValidTierData = (pricing) => {
  const rewardTiers = pricing.promotion.reward?.tiers;
  // make sure the item is not part of single-sku bulk
  return !pricing.alternate?.bulk?.value
    && (Array.isArray(rewardTiers) && rewardTiers[0] && typeof rewardTiers[0] === 'object'
      && (rewardTiers[0].minPurchaseAmount || rewardTiers[0].minPurchaseQuantity)
      && (rewardTiers[0].rewardPercent
        || rewardTiers[0].rewardAmountPerOrder
        || rewardTiers[0].rewardAmountPerItem
        || rewardTiers[0].rewardFixedPrice)
      && allTiersHaveSameDetails(rewardTiers));
};

const isValidMSBData = (pricing) => {
  const rewardTiers = pricing.promotion?.reward?.tiers;
  if (rewardTiers?.length === 1) {
    return isValidTierData(pricing);
  }
  return false;
};

const isValidBMSMData = (pricing) => {
  const rewardTiers = pricing.promotion?.reward?.tiers;
  if (
    rewardTiers?.length >= 2
    && rewardTiers.length <= MAX_NUM_TIERS_IN_PROMOTION
    && !rewardTiers[0].rewardAmountPerItem
  ) {

    if (!isValidTierData(pricing)) {
      return false;
    }

    // hide component if anchor price is less than minimum for rewardFixedPrice scenarios
    return !(rewardTiers[0].rewardFixedPrice && !(pricing.value >= getMinimumPrice(rewardTiers)));
  }

  return false;
};

const isCompatiblePromotion = (anchorItem) => {
  const pricing = anchorItem?.pricing;
  const promotion = pricing?.promotion;
  const hasConditionalPromotions = !!pricing?.conditionalPromotions?.length;
  const hasItems = (promotion?.itemList?.length || promotion?.nvalues?.length);
  const hasPreferredPricing = pricing?.preferredPriceFlag;
  if (hasPreferredPricing || !hasItems || !hasConditionalPromotions) {
    return false;
  }

  switch (promotion.experienceTag) {
  case EXPERIENCE_TAGS.BOGO:
    return isValidBogoData(promotion);
  case EXPERIENCE_TAGS.MSB:
    return isValidMSBData(pricing);
  case EXPERIENCE_TAGS.BMSM:
    return isValidBMSMData(pricing);
  default:
    return false;
  }
};

const groupItemsByBrandAndSortAlphabetically = ((products) => (
  products.slice().sort((product1, product2) => {
    const { isSoldOut: isSoldOut1, identifiers: identifiers1 } = product1;
    const { isSoldOut: isSoldOut2, identifiers: identifiers2 } = product2;

    const brand1 = identifiers1?.brandName;
    const brand2 = identifiers2?.brandName;
    const label1 = identifiers1?.productLabel;
    const label2 = identifiers2?.productLabel;

    if (isSoldOut1 < isSoldOut2) return -1;
    if (isSoldOut1 > isSoldOut2) return 1;

    if (!brand1 || !label1) return 1;
    if (!brand2 || !label2) return -1;

    if (brand1 < brand2) return -1;
    if (brand1 > brand2) return 1;

    if (label1 < label2) return -1;
    if (label1 > label2) return 1;

    return 0;
  })
));

/**
 * Keep items that have identifiers, pricing, a price greater than or equal to the minimum,
 * and a valid itemId
 * @returns {Array} filteredItems
 */
const filterItems = ({ items, minimumPrice }) => items.filter((item) => (
  item?.identifiers
  && item.pricing
  && !item.pricing.preferredPriceFlag
  && item.pricing.value >= minimumPrice
));

const addSoldOut = (product) => {
  return {
    ...product,
    isSoldOut: product.identifiers?.productType === MA ? false : !isFulfillable(product),
  };
};

const attachSoldOutToProducts = (products) => (
  products.map(addSoldOut)
);

const sortAnchorItemToTopOfList = ((items, anchorId) => items.slice().sort((item1, item2) => {
  if (item1.itemId === anchorId) return -1;
  if (item2.itemId === anchorId) return 1;
  return 0;
}));

const collectItemsMap = (items) => {
  const listItemsMap = new Map();
  let listHasFulfillment = false;

  items.forEach((item) => {
    listHasFulfillment = listHasFulfillment || !item.isSoldOut;
    listItemsMap.set(item.itemId, item);
  });

  return {
    listItemIds: [...listItemsMap.keys()],
    listItemsMap,
    listHasFulfillment
  };
};

const getSanitizedRewardTiers = (promotion, subExperience) => {
  const tiers = promotion?.reward?.tiers;
  if (!tiers) {
    const isBogoFree = subExperience === 'buyOneGetOne';
    return [{
      tier: 0,
      minPurchaseAmount: 0,
      minPurchaseQuantity: isBogoFree ? 1 : 0,
      rewardPercent: isBogoFree ? 100 : 0,
      rewardAmountPerItem: 0,
      rewardAmountPerOrder: 0,
      rewardFixedPrice: 0,
      fixedPriceTotal: 0,
    }];
  }

  return tiers
    .slice()
    // Sorts promotion tiers from smallest to largest quantity or amount needed to qualify
    .sort((
      { minPurchaseQuantity: aQty, minPurchaseAmount: aAmt },
      { minPurchaseQuantity: bQty, minPurchaseAmount: bAmt }) => {
      return (aQty < bQty || aAmt < bAmt) ? -1 : 1;
    })
    // Guarantees each tier has default value for each key and sets the tier index
    .map((tier, i) => {
      return {
        tier: i,
        minPurchaseAmount: tier.minPurchaseAmount || 0,
        minPurchaseQuantity: tier.minPurchaseQuantity || 0,
        rewardPercent: tier.rewardPercent || 0,
        rewardAmountPerItem: tier.rewardAmountPerItem || 0,
        rewardAmountPerOrder: tier.rewardAmountPerOrder || 0,
        rewardFixedPrice: tier.rewardFixedPrice || 0,
        fixedPriceTotal: Math.ceil(tier.rewardFixedPrice * tier.minPurchaseQuantity) || 0,
      };
    });
};

const getSanitizedItems = (items = [], anchorItemId, minimumPrice = 0) => {
  return pipe(
    filterItems,
    attachSoldOutToProducts,
    groupItemsByBrandAndSortAlphabetically,
    (groupedItems) => sortAnchorItemToTopOfList(groupedItems, anchorItemId),
    collectItemsMap
  )({ items, minimumPrice });
};

/**
 * Given a supported promotion, this function will return the sub-experience associated with it.
 * Will return an empty string if no sub-experience can be found.
 * @param {Object} promoData
 * @returns {string} sub-experience
 */
const getSubExperience = (promoData) => {
  if (promoData.experienceTag === EXPERIENCE_TAGS.BOGO) return promoData.subExperienceTag;

  const {
    minPurchaseQuantity,
    minPurchaseAmount,
    rewardPercent,
    rewardAmountPerItem,
    rewardAmountPerOrder,
    rewardFixedPrice
  } = promoData.reward.tiers[0];

  // We skip 3 and 6 so combo of min quantity/amount and reward matches MSB
  if (promoData.experienceTag === 'BMSM') {
    // BuyXorMoreGetYPercent
    if (minPurchaseQuantity && rewardPercent) return 'BMSM1';
    // BuyXorMoreSaveYDollar
    if (minPurchaseQuantity && rewardAmountPerOrder) return 'BMSM2';
    // BuyXDollarGetYPercent
    if (minPurchaseAmount && rewardPercent) return 'BMSM4';
    // BuyXDollarGetYDollar
    if (minPurchaseAmount && rewardAmountPerOrder) return 'BMSM5';
    // BuyXPayYDollars
    if (minPurchaseQuantity && rewardFixedPrice) return 'BMSM7';
  }

  // MSBONLINE
  if (promoData.experienceTag === 'MSBONLINE') {
    // BuyXorMoreGetYPercent
    if (minPurchaseQuantity && rewardPercent) return 'MSB1';
    // BuyXorMoreSaveYDollar
    if (minPurchaseQuantity && rewardAmountPerOrder) return 'MSB2';
    // BuyXorMoreSaveYDollarEach
    if (minPurchaseQuantity && rewardAmountPerItem) return 'MSB3';
    // BuyXDollarGetYPercent
    if (minPurchaseAmount && rewardPercent) return 'MSB4';
    // BuyXDollarGetYDollar
    if (minPurchaseAmount && rewardAmountPerOrder) return 'MSB5';
    // BuyXDollarGetYDollarEach
    if (minPurchaseAmount && rewardAmountPerItem) return 'MSB6';
  }

  return '';
};

const getAnalyticsComponentName = ({ isBogo, isMsb, isBmsm }) => {
  return (isBogo && 'bogo')
    || (isMsb && 'multi-sku bulk')
    || (isBmsm && 'buy more save more') || '';
};

const getAnalyticsSharedSection = ({ isBogo, isMsb, isBmsm }) => {
  return (isBogo && 'bogo')
    || (isMsb && 'multi-sku')
    || (isBmsm && 'buy more save more') || '';
};

const buildRefinedNValue = (originalNvalue, refinement) => {
  if (originalNvalue && refinement) {
    return `${originalNvalue}${NVALUE_REFINEMENT_SEPARATOR}${refinement}`;
  }

  return originalNvalue;
};

const isDiffLessThanAMonth = (endDate) => {
  const currDate = new Date();
  const diffInMilliseconds = endDate - currDate;
  const isPromoExpired = diffInMilliseconds < 0;
  const diffInDays = (diffInMilliseconds / (24 * 60 * 60 * 1000));
  return (!isPromoExpired && diffInDays <= 30);
};

const getExpDate = (endDate) => {
  const expDateArr = new Date(endDate).toString().split(' ');
  const day = expDateArr[2] / 10 < 1 ? expDateArr[2] % 10 : expDateArr[2];
  const expDate = ' ' + expDateArr[1] + ' ' + day;
  return expDate;
};
const transformItemDataToPromoModel = (products = [], anchorItem, isSearchData, isApplianceAnchorAvailable) => {
  const promotion = anchorItem.pricing.promotion;
  const conditionalPromotion = anchorItem.pricing?.conditionalPromotions?.[0];
  const startDate = conditionalPromotion?.dates?.start;
  const endDate = conditionalPromotion?.dates?.end;
  const isExpiring = isDiffLessThanAMonth(new Date(endDate));
  const expDate = getExpDate(endDate);
  const shortDesc = conditionalPromotion?.description?.shortDesc;
  const longDesc = conditionalPromotion?.description?.longDesc;
  const subExperience = getSubExperience(promotion);
  const rewardTiers = getSanitizedRewardTiers(promotion, subExperience);
  const isBogo = promotion.experienceTag === EXPERIENCE_TAGS.BOGO && !!subExperience;
  const isMsb = promotion.experienceTag === EXPERIENCE_TAGS.MSB && !!subExperience;
  const isBmsm = promotion.experienceTag === EXPERIENCE_TAGS.BMSM && !!subExperience;
  const isAppliance = anchorItem?.identifiers?.productType === MA;

  const analyticsComponent = getAnalyticsComponentName({ isBogo, isMsb, isBmsm });
  const analyticsSharedSection = getAnalyticsSharedSection({ isBogo, isMsb, isBmsm });

  // Find greatest rewardFixedPrice if BMSM7
  let minimumPrice = 0;
  if (subExperience === 'BMSM7') {
    minimumPrice = getMinimumPrice(rewardTiers);
  }

  // Include anchor item for search promotions
  const updatedProducts = (!isSearchData || products.find(({ itemId }) => itemId === anchorItem.itemId))
    ? products : [anchorItem, ...products.slice(0, 5)];

  const {
    listItemIds,
    listItemsMap,
    listHasFulfillment
  } = getSanitizedItems(updatedProducts, anchorItem.itemId, minimumPrice);

  if (!listItemsMap.has(anchorItem.itemId)) {
    listItemsMap.set(anchorItem.itemId, addSoldOut(anchorItem));
  }

  const isAnchorFulfillable = (isBogo && isAppliance)
    ? isApplianceAnchorAvailable : !listItemsMap.get(anchorItem.itemId).isSoldOut;

  return {
    allListProductsAndAnchor: listItemsMap,
    isBogo,
    isMsb,
    isBmsm,
    startDate,
    endDate,
    expDate,
    isExpiring,
    shortDesc,
    longDesc,
    analyticsComponent,
    analyticsSharedSection,
    displayableItemIds: listItemIds,
    listHasFulfillment,
    isAnchorFulfillable,
    subExperience,
    rewardTiers,
  };
};

export {
  isCompatiblePromotion,
  transformItemDataToPromoModel,
  getSanitizedItems,
  buildRefinedNValue,
};
